import { ApolloError, Reference, gql, useMutation } from "@apollo/client";
import {useCallback, useMemo} from "react";
import { toast } from "react-toastify";
import {
  NOTIFICATION_CATEGORY,
  NOTIFICATION_STATUS,
  NotificationAssignment,
} from "types/notifications";

interface UpdateAssignedNotificationsData {
  updatedAssignedNotifications: {
    result: Pick<
      NotificationAssignment,
      "id" | "status" | "modifiedAt" | "reviewedAt"
    >[];
  };
}
interface UpdateAssignedNotificationsVars {
  assignedNotifications?: Pick<NotificationAssignment, "id">[] | null;
  categories?: NOTIFICATION_CATEGORY[] | null;
  status: NotificationAssignment["status"];
}

const UPDATE_ASSIGNED_NOTIFICATIONS = gql`
  mutation updateAssignedNotification(
    $assignedNotifications: [UpdateAssignedNotificationsInputType]!
    $status: String!
  ) {
    updatedAssignedNotifications: update_assigned_notifications(
      notification_assignments: $assignedNotifications
      status: $status
    ) {
      result {
        id
        status
        modifiedAt: modified_at
        reviewedAt: reviewed_at
      }
    }
  }
`;

const UPDATE_ASSIGNED_NOTIFICATIONS_BY_CATEGORY = gql`
  mutation updateAssignedNotification(
    $categories: [String]!
    $status: String!
  ) {
    updatedAssignedNotifications: update_assigned_notifications(
      categories: $categories
      status: $status
    ) {
      result {
        id
        status
        modifiedAt: modified_at
        reviewedAt: reviewed_at
      }
    }
  }
`;

export const useUpdateAssignedNotifications = (updateByCategory: boolean) => {
  // No `useAuthContext()` here any more. The identity was needed only to build
  // variables for the hand-rolled cache patch; refetching by operation name lets
  // Apollo reuse each active query's own variables instead.
  const updateQuery = useMemo(() => {
    return updateByCategory ? UPDATE_ASSIGNED_NOTIFICATIONS_BY_CATEGORY : UPDATE_ASSIGNED_NOTIFICATIONS;
  }, [updateByCategory]);

  const [updateNotificationAssignments] = useMutation<
    UpdateAssignedNotificationsData,
    UpdateAssignedNotificationsVars
  >(updateQuery, {
    // 🐛 The counters used to be decremented by hand in `cache.updateQuery`, and
    // that was broken in FOUR ways, three of them invisible and one fatal:
    //   1. it read `unreadCounter`, which this query does not select;
    //   2. the counters are `{ aggregate: { count } }` objects, so
    //      `data.allCounter - 1` evaluated to **NaN**;
    //   3. it returned an object of counters only, so writing it back dropped
    //      `notificationsAll` and the other three lists from the cached entry;
    //   4. and none of that ever ran, because it addressed the cached query with
    //      `{ employeeId }` while the query is keyed on `{ limit, offsetAll,
    //      offsetEmployeeAndProjects, offsetTimesheets, offsetProcesses,
    //      employeeId }`. The read came back incomplete, so the callback returned
    //      early and wrote nothing. The visible symptom was that dismissing a
    //      notification never cleared the badge, while the write itself succeeded.
    //
    // Refetching by OPERATION NAME fixes the class rather than the instance:
    // Apollo matches active queries by name and reuses **their own** variables, so
    // the paging state never has to be threaded down here. It also stops this file
    // maintaining a hand-rolled copy of a figure the server computes — which is
    // what produced all four defects.
    //
    // ⚠️ Costs one round trip per dismiss. Accepted: the alternative is patching
    // an aggregate the client cannot actually derive.
    refetchQueries: ["getEmployeeAllNotifications", "getManagerAllNotifications"],
    awaitRefetchQueries: true,
    update(cache, { data }) {
      const notifications = data?.updatedAssignedNotifications.result ?? [];
      if (notifications.length > 0) {
        notifications.forEach((notification) => {
          if (notification.status === NOTIFICATION_STATUS.DELETED) {
            cache.modify({
              fields: {
                notifications_assignments(existing = [], { readField }) {
                  return existing.filter(
                    (ref: Reference) => notification.id !== readField("id", ref)
                  );
                },
              },
            });
          } else {
            cache.writeFragment({
              id: `notifications_assignments:${notification.id}`,
              fragment: gql`
                fragment Notification on notifications_assignments {
                  status
                  modified_at
                  reviewed_at
                }
              `,
              data: {
                status: notification.status,
                modified_at: notification.modifiedAt,
                reviewed_at: notification.reviewedAt,
              },
            });
            // The counters are server-computed aggregates and are NOT patched
            // here — `refetchQueries` above says why hand-maintaining them was the
            // wrong shape. This fragment write is kept: it flips the row's own
            // status so the list item reads as read immediately, which the refetch
            // would otherwise only confirm a round trip later.
          }
        });
      }
    },
  });

  const updateAssignedNotifications = useCallback(
    async (variables: UpdateAssignedNotificationsVars) => {
      try {
        await updateNotificationAssignments({ variables });
      } catch (ex) {
        toast.error((ex as ApolloError)?.message);
      }
    },
    [updateNotificationAssignments]
  );

  return { updateAssignedNotifications };
};
